import { useCallback, useEffect, useMemo, useState } from 'react';
import { useBusinessStore } from '../store/useBusinessStore';
import { useAuthStore } from '../store/useAuthStore';
import { supabase } from '../supabaseClient';
import toast from 'react-hot-toast';
import { buildCustomerStats, phoneDigits, phoneKey } from '../utils/customers';
import { isModuleEnabled } from '../utils/businessConfigHelpers';

const DEMO_BUSINESS_IDS = ['9001', '9002', '9003'];

export const useCustomers = () => {
  const { masterDb, activeBusinessId } = useBusinessStore();
  const { userRole } = useAuthStore();
  const isReadOnlyDemo = DEMO_BUSINESS_IDS.includes(String(activeBusinessId)) && userRole !== 'master';

  const activeBusiness = Array.isArray(masterDb)
    ? masterDb.find(b => String(b.id) === String(activeBusinessId))
    : null;

  // Programa de sellos del negocio (módulo Fidelización). Sin módulo o desactivado, no se muestra nada por cliente.
  const loyaltyConfig = activeBusiness?.loyalty_config;
  const loyalty = activeBusiness?.feature_loyalty === false || loyaltyConfig?.enabled === false
    ? null
    : { visitsRequired: loyaltyConfig?.visitsRequired || 5, rewardLabel: loyaltyConfig?.rewardLabel || '1 servicio gratis' };

  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tableMissing, setTableMissing] = useState(false);

  // Datos fiscales (módulo Cotizaciones) y lista de precios por cliente (Catálogo o Cotizaciones). Sin esos módulos no se piden ni se muestran.
  const fiscal = isModuleEnabled(activeBusiness, 'feature_cotizaciones');
  const hasLists = fiscal || isModuleEnabled(activeBusiness, 'feature_catalogo');
  const [priceLists, setPriceLists] = useState([]);
  useEffect(() => {
    if (!activeBusinessId || !hasLists) { setPriceLists([]); return undefined; }
    let alive = true;
    supabase.from('price_lists').select('key, name, is_default').eq('business_id', String(activeBusinessId)).order('created_at')
      .then(({ data, error }) => { if (alive) setPriceLists(error ? [] : data || []); });
    return () => { alive = false; };
  }, [activeBusinessId, hasLists]);

  const load = useCallback(async () => {
    if (!activeBusinessId) return;
    setLoading(true);
    const { data, error } = await supabase
      .from('customers')
      .select('*')
      .eq('business_id', String(activeBusinessId))
      .order('last_contact_at', { ascending: false });

    if (error) {
      // 42P01 / PGRST205: la migración aún no se ha ejecutado en Supabase.
      setTableMissing(['42P01', 'PGRST205'].includes(error.code) || /customers/i.test(error.message || ''));
      setCustomers([]);
    } else {
      setTableMissing(false);
      setCustomers(Array.isArray(data) ? data : []);
    }
    setLoading(false);
  }, [activeBusinessId]);

  useEffect(() => { load(); }, [load]);

  const statsByKey = useMemo(
    () => buildCustomerStats(customers, activeBusiness?.appointments, activeBusiness?.orders),
    [customers, activeBusiness]
  );

  const guardDemo = () => {
    if (isReadOnlyDemo) {
      toast.error('Modo solo lectura: las cuentas demo no se pueden modificar.');
      return true;
    }
    return false;
  };

  const addCustomer = async ({ name, phone, email, notes }) => {
    if (guardDemo()) return false;
    const digits = phoneDigits(phone);
    if (digits.length < 7) {
      toast.error('Ingresa un teléfono válido (con indicativo, ej. 573001112233).');
      return false;
    }
    const { data, error } = await supabase
      .from('customers')
      .insert([{
        business_id: String(activeBusinessId), name: name?.trim() || null, phone: digits,
        email: email?.trim() || null, notes: notes?.trim() || null, source: 'manual'
      }])
      .select()
      .single();
    if (error) {
      toast.error(error.code === '23505' ? 'Ya existe un cliente con ese teléfono.' : 'No se pudo guardar el cliente.');
      return false;
    }
    setCustomers(prev => [data, ...prev]);
    toast.success('Cliente guardado');
    return true;
  };

  const updateCustomer = async (id, changes) => {
    if (guardDemo()) return false;
    const previous = customers;
    const patch = { ...changes, updated_at: new Date().toISOString() };
    setCustomers(prev => prev.map(c => (c.id === id ? { ...c, ...patch } : c)));
    const { error } = await supabase.from('customers').update(patch).eq('id', id);
    if (error) {
      setCustomers(previous);
      toast.error(/next_follow_up_at|loyalty_redeemed/.test(error.message || '')
        ? 'Falta ejecutar una migración en Supabase (20260930c_customers_followup.sql o 20260930d_customers_loyalty.sql).'
        : /tax_id|legal_name|billing_|price_list_key/.test(error.message || '')
        ? 'Falta ejecutar una migración en Supabase (20261003a_catalog.sql o 20261003b_billing_base.sql).'
        : 'No se pudo guardar el cambio. Intenta de nuevo.');
      return false;
    }
    toast.success('Ficha actualizada');
    return true;
  };

  // Importación masiva (Clientes y Campañas): agrega los teléfonos que todavía no están en la base. Si la fila trae lista de precios
  // (price_list_key), se asigna también a los clientes que ya existían, para poder cargar «todos los clientes con su lista» de una vez.
  // Devuelve cuántos clientes nuevos se agregaron.
  const importCustomers = async (list) => {
    if (guardDemo()) return 0;
    const known = new Map(customers.map(c => [phoneKey(c.phone), c]));
    const rows = [];
    const listByCustomer = new Map(); // id del cliente existente → lista
    (list || []).forEach(({ name, phone, email, price_list_key: listKey }) => {
      const digits = phoneDigits(phone);
      const key = phoneKey(digits);
      if (digits.length < 7) return;
      const existing = known.get(key);
      if (existing) {
        if (listKey && existing.price_list_key !== listKey) listByCustomer.set(existing.id, listKey);
        return;
      }
      known.set(key, { id: null, price_list_key: listKey || null });
      rows.push({
        business_id: String(activeBusinessId), name: name?.trim() || null, phone: digits, email: email?.trim() || null, source: 'manual',
        ...(listKey ? { price_list_key: listKey } : {})
      });
    });
    let updated = 0;
    if (listByCustomer.size > 0) {
      const idsByList = new Map();
      listByCustomer.forEach((listKey, id) => { if (!idsByList.has(listKey)) idsByList.set(listKey, []); idsByList.get(listKey).push(id); });
      for (const [listKey, ids] of idsByList) {
        const { error } = await supabase.from('customers').update({ price_list_key: listKey }).eq('business_id', String(activeBusinessId)).in('id', ids);
        if (error) { toast.error('No se pudo asignar la lista de precios a los clientes que ya existían.'); break; }
        updated += ids.length;
      }
    }
    if (rows.length === 0) {
      if (updated > 0) {
        toast.success(`${updated} cliente${updated === 1 ? '' : 's'} que ya existían quedaron con su lista de precios.`);
        await load();
      } else toast('Esos contactos ya estaban en tu base.');
      return 0;
    }
    const { error } = await supabase.from('customers').insert(rows);
    if (error) {
      toast.error('No se pudieron importar los contactos.');
      return 0;
    }
    if (updated > 0) toast.success(`${updated} cliente${updated === 1 ? '' : 's'} que ya existían quedaron con su lista de precios.`);
    await load();
    return rows.length;
  };

  // Vincula el teléfono real a un contacto que solo tiene identificador interno de WhatsApp (@lid).
  // Si ya existe un cliente con ese teléfono, el servidor los fusiona.
  const linkPhone = async (id, phone) => {
    if (guardDemo()) return false;
    const digits = phoneDigits(phone);
    if (digits.length < 7) {
      toast.error('Ingresa un teléfono válido (con indicativo, ej. 573001112233).');
      return false;
    }
    const { error } = await supabase.rpc('link_customer_phone', { p_id: id, p_phone: digits });
    if (error) {
      const missing = error.code === 'PGRST202' || /link_customer_phone/i.test(error.message || '');
      toast.error(missing
        ? 'Falta ejecutar la migración 20260930_customers_lid.sql en Supabase.'
        : 'No se pudo vincular el teléfono. Intenta de nuevo.');
      return false;
    }
    await load();
    toast.success('Teléfono vinculado');
    return true;
  };

  const deleteCustomer = async (id) => {
    if (guardDemo()) return false;
    const target = customers.find(c => c.id === id);
    const { error } = await supabase.from('customers').delete().eq('id', id);
    if (error) {
      toast.error('No se pudo eliminar el cliente.');
      return false;
    }
    // Su conversación también se borra (son datos personales). Si la tabla aún no existe, no pasa nada.
    if (target) {
      const lid = target.lid || (target.tags?.includes('lid') ? target.phone : null);
      const key = target.tags?.includes('lid') ? '' : phoneKey(target.phone);
      const filters = [key && `phone_key.eq.${key}`, lid && `lid.eq.${lid}`].filter(Boolean).join(',');
      if (filters) await supabase.from('customer_messages').delete().eq('business_id', String(activeBusinessId)).or(filters);
    }
    setCustomers(prev => prev.filter(c => c.id !== id));
    toast.success('Cliente eliminado');
    return true;
  };

  return {
    customers, statsByKey, loading, tableMissing, isReadOnlyDemo, loyalty, fiscal, priceLists,
    reload: load, addCustomer, updateCustomer, linkPhone, importCustomers, deleteCustomer
  };
};
